import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Download, ExternalLink, FileText, Image, Link as LinkIcon, User, Building, MapPin, Globe, Calendar, MessageSquare, Edit, Trash2, X, Search, Share2, Newspaper, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const itemTypeConfig = {
  publication: { label: 'Digital PR', color: 'bg-blue-100 text-blue-700' },
  social_page: { label: 'Social', color: 'bg-pink-100 text-pink-700' },
  print_media: { label: 'Print', color: 'bg-amber-100 text-amber-700' },
  bundle: { label: 'Bundle', color: 'bg-purple-100 text-purple-700' }
};

const AdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [liveUrl, setLiveUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_company: '',
    customer_gstin: '',
    customer_address: '',
    total_amount: '',
    deadline_date: '',
    notes: ''
  });
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/orders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrderDetails(response.data);
      setNewStatus(response.data.order.order_status);
      setLiveUrl(response.data.order.live_url || '');
      
      // Fetch submission data
      try {
        const submissionResponse = await axios.get(`${API}/orders/${orderId}/submission`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubmission(submissionResponse.data);
      } catch (subError) {
        // No submission yet
        setSubmission(null);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load order details');
    }
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    fetchOrderDetails(order.id);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(
        `${API}/admin/orders/${selectedOrder.id}/status`,
        {
          status: newStatus,
          message: statusMessage || undefined,
          live_url: liveUrl || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Status updated successfully');
      setStatusMessage('');
      fetchOrders();
      fetchOrderDetails(selectedOrder.id);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(
        `${API}/admin/orders/${orderId}/invoice`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { pdf, filename } = response.data;
      const blob = new Blob([Uint8Array.from(atob(pdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const openEditModal = () => {
    if (!orderDetails) return;
    
    setEditForm({
      customer_name: orderDetails.order.customer_name || '',
      customer_email: orderDetails.order.customer_email || '',
      customer_phone: orderDetails.order.customer_phone || '',
      customer_company: orderDetails.order.customer_company || '',
      customer_gstin: orderDetails.order.customer_gstin || '',
      customer_address: orderDetails.order.customer_address || '',
      total_amount: orderDetails.order.total_amount || '',
      deadline_date: orderDetails.order.deadline_date ? orderDetails.order.deadline_date.split('T')[0] : '',
      notes: orderDetails.order.admin_notes || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(
        `${API}/admin/orders/${selectedOrder.id}`,
        {
          customer_name: editForm.customer_name,
          customer_email: editForm.customer_email,
          customer_phone: editForm.customer_phone,
          customer_company: editForm.customer_company,
          customer_gstin: editForm.customer_gstin,
          customer_address: editForm.customer_address,
          total_amount: parseFloat(editForm.total_amount) || null,
          deadline_date: editForm.deadline_date || null,
          notes: editForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Order updated successfully');
      setShowEditModal(false);
      fetchOrders();
      fetchOrderDetails(selectedOrder.id);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const handleDeleteOrder = async () => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(
        `${API}/admin/orders/${selectedOrder.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Order deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedOrder(null);
      setOrderDetails(null);
      setSubmission(null);
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'created': 'bg-gray-100 text-gray-800',
      'paid': 'bg-blue-100 text-blue-800',
      'payment_failed': 'bg-red-100 text-red-800',
      'content_received': 'bg-yellow-100 text-yellow-800',
      'under_review': 'bg-purple-100 text-purple-800',
      'submitted_to_publisher': 'bg-indigo-100 text-indigo-800',
      'live': 'bg-green-100 text-green-800',
      'completed': 'bg-black text-white',
      'cancelled': 'bg-gray-500 text-white',
      'refunded': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-playfair text-4xl font-bold mb-2">Orders</h1>
        <p className="font-manrope text-sm text-gray-600">Manage and track customer orders</p>
      </div>
      
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Search Orders</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by order ID, customer name, email, phone..."
              data-testid="admin-orders-search"
              className="w-full border border-gray-200 pl-10 pr-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Filter by Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-status-filter"
            className="border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
          >
            <option value="">All Orders</option>
            <option value="paid">Paid</option>
            <option value="payment_failed">Payment Failed</option>
            <option value="content_received">Content Received</option>
            <option value="under_review">Under Review</option>
            <option value="submitted_to_publisher">Submitted to Publisher</option>
            <option value="live">Live</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          {(() => {
            const filteredOrders = orders.filter(order => {
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              const itemName = order.item_name || order.publication_name || '';
              return (
                order.id.toLowerCase().includes(query) ||
                (order.customer_name && order.customer_name.toLowerCase().includes(query)) ||
                (order.customer_email && order.customer_email.toLowerCase().includes(query)) ||
                (order.customer_phone && order.customer_phone.toLowerCase().includes(query)) ||
                (itemName && itemName.toLowerCase().includes(query)) ||
                (order.invoice_id && order.invoice_id.toLowerCase().includes(query))
              );
            });
            
            return (
              <>
                <h2 className="font-playfair text-2xl font-bold mb-6">
                  Orders ({filteredOrders.length}{searchQuery && ` of ${orders.length}`})
                </h2>
                
                {loading ? (
                  <p className="font-manrope text-gray-600">Loading...</p>
                ) : filteredOrders.length === 0 ? (
                  <p className="font-manrope text-gray-600">
                    {searchQuery ? 'No orders match your search' : 'No orders found'}
                  </p>
                ) : (
                  <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
                    {filteredOrders.map(order => (
                      <button
                        key={order.id}
                        onClick={() => handleSelectOrder(order)}
                        data-testid={`order-item-${order.id}`}
                        className={`w-full border p-4 text-left transition-all duration-300 ${
                          selectedOrder?.id === order.id 
                            ? 'border-black bg-black text-white' 
                            : 'border-gray-200 hover:border-black'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-mono text-xs">{order.id.substring(0, 8)}</span>
                          <div className="flex gap-2">
                            {order.has_submission && (
                              <span className={`px-2 py-1 text-xs font-manrope uppercase tracking-widest ${
                                selectedOrder?.id === order.id ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800'
                              }`}>
                                Content
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-manrope uppercase tracking-widest ${
                        selectedOrder?.id === order.id ? 'bg-white text-black' : getStatusColor(order.order_status)
                      }`}>
                        {order.order_status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    {order.item_type && (
                      <span className={`px-1.5 py-0.5 text-xs font-manrope rounded ${
                        itemTypeConfig[order.item_type]?.color || 'bg-gray-100 text-gray-700'
                      }`}>
                        {itemTypeConfig[order.item_type]?.label || order.item_type}
                      </span>
                    )}
                    <span className="font-manrope text-sm font-semibold">
                      {order.item_name || order.publication_name || 'Unknown'}
                    </span>
                  </div>
                  <div className="font-manrope text-sm">
                    {order.customer_name}
                  </div>
                  <div className="font-mono text-xs mt-2">
                    ₹{order.total_amount.toLocaleString('en-IN')}
                  </div>
                </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        
        <div>
          {selectedOrder && orderDetails ? (
            <div className="border border-gray-200 p-6 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <h2 className="font-playfair text-2xl font-bold">Order Details</h2>
                <div className="flex gap-2">
                  <button
                    onClick={openEditModal}
                    data-testid="edit-order-button"
                    className="p-2 border border-gray-200 hover:border-black hover:bg-black hover:text-white transition-all duration-300"
                    title="Edit Order"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="delete-order-button"
                    className="p-2 border border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-300"
                    title="Delete Order"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <div>
                  <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Order ID</span>
                  <span className="font-mono text-sm">{selectedOrder.id}</span>
                </div>
                
                <div>
                  <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Payment Status</span>
                  <span className={`inline-block px-2 py-1 text-xs font-manrope uppercase tracking-widest ${
                    orderDetails.order.payment_status === 'paid' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {orderDetails.order.payment_status}
                  </span>
                  {orderDetails.order.webhook_verified && (
                    <span className="ml-2 text-xs text-gray-600">(Webhook Verified)</span>
                  )}
                </div>
                
                {orderDetails.order.razorpay_order_id && (
                  <div>
                    <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Razorpay Order ID</span>
                    <span className="font-mono text-xs break-all">{orderDetails.order.razorpay_order_id}</span>
                  </div>
                )}
                
                {orderDetails.order.razorpay_payment_id && (
                  <div>
                    <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Razorpay Payment ID</span>
                    <span className="font-mono text-xs break-all">{orderDetails.order.razorpay_payment_id}</span>
                  </div>
                )}
                
                {orderDetails.order.payment_verified_at && (
                  <div>
                    <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Payment Verified At</span>
                    <span className="font-manrope text-xs">
                      {new Date(orderDetails.order.payment_verified_at).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                
                <div>
                  <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Customer</span>
                  <div className="font-manrope text-sm">
                    <div className="font-semibold">{orderDetails.order.customer_name}</div>
                    <div>{orderDetails.order.customer_email}</div>
                    <div>{orderDetails.order.customer_phone}</div>
                  </div>
                </div>
                
                <div>
                  <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Publication</span>
                  <span className="font-manrope text-sm font-semibold">{orderDetails.publication.name}</span>
                </div>
                
                <div>
                  <span className="font-manrope text-xs text-gray-600 uppercase tracking-widest block mb-1">Package</span>
                  <span className="font-manrope text-sm">{orderDetails.package.name}</span>
                </div>
              </div>
              
              {/* Submitted Content Section */}
              {submission ? (
                <>
                  <hr className="border-gray-200 my-6" />
                  <div className="mb-6">
                    <h3 className="font-playfair text-xl font-bold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Submitted Content
                    </h3>
                    
                    {/* Brand Information */}
                    <div className="bg-gray-50 p-4 mb-4">
                      <h4 className="font-manrope text-xs text-gray-600 uppercase tracking-widest mb-3">Brand Information</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {submission.brand_name && (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="font-semibold">{submission.brand_name}</span>
                          </div>
                        )}
                        {submission.spokesperson_name && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{submission.spokesperson_name}</span>
                            {submission.spokesperson_title && <span className="text-gray-500">({submission.spokesperson_title})</span>}
                          </div>
                        )}
                        {submission.city && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span>{submission.city}</span>
                          </div>
                        )}
                        {submission.website_url && (
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-gray-400" />
                            <a href={submission.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                              {submission.website_url}
                            </a>
                          </div>
                        )}
                        {submission.preferred_publish_date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>Preferred: {new Date(submission.preferred_publish_date).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Article Content */}
                    {submission.content_text && (
                      <div className="mb-4">
                        <h4 className="font-manrope text-xs text-gray-600 uppercase tracking-widest mb-2">Article Content</h4>
                        <div className="bg-white border border-gray-200 p-4 max-h-48 overflow-y-auto">
                          <p className="font-manrope text-sm whitespace-pre-wrap">{submission.content_text}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Writing Brief (if any) */}
                    {submission.writing_brief && (
                      <div className="mb-4 bg-blue-50 p-4 border border-blue-100">
                        <h4 className="font-manrope text-xs text-blue-800 uppercase tracking-widest mb-3">Writing Brief</h4>
                        <div className="space-y-2 text-sm">
                          {submission.writing_brief.brand_story && (
                            <div>
                              <span className="font-semibold">Brand Story:</span>
                              <p className="text-gray-700">{submission.writing_brief.brand_story}</p>
                            </div>
                          )}
                          {submission.writing_brief.key_angle && (
                            <div>
                              <span className="font-semibold">Key Angle:</span>
                              <p className="text-gray-700">{submission.writing_brief.key_angle}</p>
                            </div>
                          )}
                          {submission.writing_brief.must_include_points && (
                            <div>
                              <span className="font-semibold">Must Include:</span>
                              <p className="text-gray-700">{submission.writing_brief.must_include_points}</p>
                            </div>
                          )}
                          {submission.writing_brief.tone && (
                            <div>
                              <span className="font-semibold">Tone:</span>
                              <span className="ml-2 capitalize">{submission.writing_brief.tone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Links */}
                    {submission.links && submission.links.length > 0 && submission.links.some(l => l.url) && (
                      <div className="mb-4">
                        <h4 className="font-manrope text-xs text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                          <LinkIcon className="w-4 h-4" />
                          Links to Include
                        </h4>
                        <div className="space-y-2">
                          {submission.links.filter(l => l.url).map((link, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 p-2">
                              <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate flex-1">
                                {link.url}
                              </a>
                              {link.anchor && (
                                <span className="text-gray-500 text-xs">Anchor: &ldquo;{link.anchor}&rdquo;</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Images */}
                    {submission.images && submission.images.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-manrope text-xs text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Images ({submission.images.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {submission.images.map((img, idx) => (
                            <a key={idx} href={img} target="_blank" rel="noopener noreferrer" className="block">
                              <img src={img} alt={`Submission ${idx + 1}`} className="w-full h-20 object-cover border border-gray-200 hover:border-black transition-colors" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Documents */}
                    {submission.content_files && submission.content_files.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-manrope text-xs text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Documents ({submission.content_files.length})
                        </h4>
                        <div className="space-y-2">
                          {submission.content_files.map((file, idx) => (
                            <a 
                              key={idx} 
                              href={file} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-gray-50 hover:bg-gray-100 transition-colors text-sm"
                            >
                              <FileText className="w-4 h-4 text-gray-600" />
                              <span className="truncate">{file.split('/').pop()}</span>
                              <Download className="w-4 h-4 ml-auto" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Notes */}
                    {submission.notes && (
                      <div className="mb-4">
                        <h4 className="font-manrope text-xs text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Notes for Editor
                        </h4>
                        <div className="bg-yellow-50 border border-yellow-100 p-3 text-sm">
                          {submission.notes}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : orderDetails.order.order_status !== 'paid' && orderDetails.order.payment_status === 'paid' ? (
                <>
                  <hr className="border-gray-200 my-6" />
                  <div className="bg-yellow-50 border border-yellow-200 p-4 text-center">
                    <p className="font-manrope text-sm text-yellow-800">
                      Waiting for customer to submit content
                    </p>
                  </div>
                </>
              ) : null}
              
              <hr className="border-gray-200 my-6" />
              
              <div className="mb-6">
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Update Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  data-testid="admin-update-status-select"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none mb-3"
                >
                  <option value="paid">Paid</option>
                  <option value="payment_failed">Payment Failed</option>
                  <option value="content_received">Content Received</option>
                  <option value="under_review">Under Editorial Review</option>
                  <option value="submitted_to_publisher">Submitted to Publisher</option>
                  <option value="live">Live</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refunded">Refunded</option>
                </select>
                
                <input
                  type="text"
                  value={statusMessage}
                  onChange={(e) => setStatusMessage(e.target.value)}
                  placeholder="Status message (optional)"
                  data-testid="admin-status-message-input"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none mb-3"
                />
                
                {(newStatus === 'live' || newStatus === 'completed') && (
                  <input
                    type="url"
                    value={liveUrl}
                    onChange={(e) => setLiveUrl(e.target.value)}
                    placeholder="Live article URL"
                    data-testid="admin-live-url-input"
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none mb-3"
                  />
                )}
                
                <button
                  onClick={handleUpdateStatus}
                  data-testid="admin-update-status-button"
                  className="w-full px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
                >
                  Update Status
                </button>
              </div>
              
              <hr className="border-gray-200 my-6" />
              
              <div className="space-y-3">
                <button
                  onClick={() => handleDownloadInvoice(selectedOrder.id)}
                  data-testid="admin-download-invoice-button"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-black text-black font-manrope text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
                >
                  <Download className="w-4 h-4" />
                  Download Invoice
                </button>
                
                <button
                  onClick={() => navigate(`/order/${selectedOrder.id}`)}
                  data-testid="admin-view-order-button"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-200 text-black font-manrope text-xs uppercase tracking-widest hover:border-black transition-all duration-300"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Customer View
                </button>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 p-12 text-center">
              <p className="font-manrope text-gray-600">Select an order to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Order Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="font-playfair text-xl font-bold">Edit Order</h3>
              <button 
                onClick={() => setShowEditModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Customer Name</label>
                <input
                  type="text"
                  value={editForm.customer_name}
                  onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})}
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Email</label>
                  <input
                    type="email"
                    value={editForm.customer_email}
                    onChange={(e) => setEditForm({...editForm, customer_email: e.target.value})}
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Phone</label>
                  <input
                    type="text"
                    value={editForm.customer_phone}
                    onChange={(e) => setEditForm({...editForm, customer_phone: e.target.value})}
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Company</label>
                  <input
                    type="text"
                    value={editForm.customer_company}
                    onChange={(e) => setEditForm({...editForm, customer_company: e.target.value})}
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">GSTIN</label>
                  <input
                    type="text"
                    value={editForm.customer_gstin}
                    onChange={(e) => setEditForm({...editForm, customer_gstin: e.target.value})}
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Address</label>
                <textarea
                  value={editForm.customer_address}
                  onChange={(e) => setEditForm({...editForm, customer_address: e.target.value})}
                  rows="2"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Total Amount (₹)</label>
                  <input
                    type="number"
                    value={editForm.total_amount}
                    onChange={(e) => setEditForm({...editForm, total_amount: e.target.value})}
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Deadline Date</label>
                  <input
                    type="date"
                    value={editForm.deadline_date}
                    onChange={(e) => setEditForm({...editForm, deadline_date: e.target.value})}
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-1 block uppercase tracking-widest">Admin Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  rows="3"
                  placeholder="Internal notes (not visible to customer)"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-playfair text-xl font-bold mb-2">Delete Order</h3>
              <p className="font-manrope text-sm text-gray-600">
                Are you sure you want to delete this order? This action cannot be undone.
              </p>
              <p className="font-mono text-xs mt-2 text-gray-500">
                Order ID: {selectedOrder?.id?.substring(0, 8)}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-6 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteOrder}
                className="flex-1 px-6 py-3 bg-red-600 text-white font-manrope text-xs uppercase tracking-widest hover:bg-red-700 transition-all duration-300"
              >
                Delete Order
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;